import { type ReactNode, useCallback } from "react";
import { styled } from "styled-components";

import { Spinner } from "common/components";
import { useAutocomplete } from "common/components/form/Autocomplete/useAutocomplete";
import { UIInput } from "common/components/form/UIInput";
import { createScrollbarStyle } from "common/styles";
import { useTranslate } from "common/utilities/useTranslate";
import { SearchDropdownCard } from "domains/collection/components/edit/parts/SearchDropdownCard";

const StyledContainer = styled.div`
  position: relative;
`;

const StyledResultsContainer = styled.div<{ height?: string | number }>`
  background-color: ${({ theme }) => theme.colors.searchDropdownBackground};
  border-radius: 5px;
  box-shadow: 0 0.5rem 1rem 0 rgba(0, 0, 0, 0.2);
  height: ${({ height }) => (height ? (typeof height === "number" ? `${height}px` : height) : "auto")};
  max-height: 28.25rem;
  overflow-y: auto;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  position: absolute;
  top: 54px;
  width: calc(100% - 1.75rem);
  z-index: 1;

  ${createScrollbarStyle()}

  * {
    border-radius: 5px;
  }
`;

const StyledDropdownCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledSpinner = styled(Spinner)`
  color: ${({ theme }) => theme.colors.textBright};
  display: block;
  margin: 3rem auto;
`;

const StyledNoResultsText = styled.div`
  color: ${({ theme }) => theme.colors.textBright};
`;

type AutocompleteProps = {
  id: string;
  value: string;
  placeholder?: string;
  isLoading?: boolean;
  options: { icon: ReactNode; iconLabel: string; image?: string; name: string; onClick: () => void; id: string }[];
  isFetchingNextPage?: boolean;
  resultsContainerHeight?: string | number;
  onChange: (value: string) => void;
  onClear?: () => void;
  onEndReached?: () => void;
};

export const Autocomplete = ({
  id,
  value,
  placeholder,
  isLoading,
  options,
  isFetchingNextPage,
  resultsContainerHeight,
  onClear,
  onChange,
  onEndReached,
}: AutocompleteProps) => {
  const translate = useTranslate();

  const {
    containerRef,
    resultsContainerRef,
    bottomOfSearchResultsRef,
    areOptionsVisible,
    setAreOptionsVisible,
    clearValueAndHideResults,
    updateValueAndShowResults,
    openOrCloseResults,
    closeResults,
  } = useAutocomplete({ onChange, onClear, onEndReached });

  const renderOptions = useCallback(() => {
    if (isLoading) return <StyledSpinner height={30} width={30} />;

    if (options.length === 0)
      return <StyledNoResultsText>{translate("app.search.results.no_results")}</StyledNoResultsText>;

    return (
      <StyledDropdownCardsContainer>
        {options.map(({ id, ...rest }) => (
          <SearchDropdownCard {...rest} key={id} />
        ))}
        {isFetchingNextPage && <StyledSpinner height={30} width={30} />}
      </StyledDropdownCardsContainer>
    );
  }, [isFetchingNextPage, isLoading, options, translate]);

  return (
    <StyledContainer ref={containerRef}>
      <UIInput
        clear={clearValueAndHideResults}
        isPending={isLoading}
        onChange={updateValueAndShowResults}
        onClick={() => setAreOptionsVisible(true)}
        onKeyDown={openOrCloseResults}
        placeholder={placeholder}
        value={value}
        name={id}
      />
      {areOptionsVisible && (
        <StyledResultsContainer height={resultsContainerHeight} ref={resultsContainerRef} onKeyDown={closeResults}>
          {renderOptions()}
          <div ref={bottomOfSearchResultsRef} />
        </StyledResultsContainer>
      )}
    </StyledContainer>
  );
};
